import type { IframeProps, ViewContext } from '../custom-ui/iframe/types';
import type { ThemeState } from '@atlaskit/tokens';
import type { CoreDataInner, ProductEnvironment } from '@atlassian/forge-ui-types';
import {
	getResolverContext,
	getViewContext,
	getBrowserTimezone,
	getBrowserLocale,
} from '../custom-ui/iframe/utils';
import type { BridgeContext, BridgeServices, BridgeControls } from './types';
import type { BridgeResource, ClaimBridgeResource } from './bridge-resource';
import type { ExtensionDetails } from './types';
import { parseExtensionId } from '../utils';
import { emit as emitEvent } from '../custom-ui/bridge/methods/events';
import { logBridgeContextChange } from './BridgeContextDebug';
import { computeBridgeContextChanges, type BridgeContextChanges } from './bridgeContextChangeDetection';

/**
 * A single lazily-computed slot. Call `get(factory)` to read (computing on first access),
 * and `invalidate()` to clear so the next `get()` recomputes.
 *
 * Note: `T` must not be `null` — `null` is used as the uncomputed sentinel.
 */
class LazyCell<T extends NonNullable<unknown>> {
	private value: T | null = null;

	get(compute: () => T): T {
		if (this.value === null) {
			this.value = compute();
		}
		return this.value;
	}

	invalidate(): void {
		this.value = null;
	}
}

export type BridgeContextInput = Pick<
	IframeProps,
	| 'accountId'
	| 'bridge'
	| 'coreData'
	| 'extension'
	| 'extensionData'
	| 'extensionPayload'
	| 'extensionViewData'
	| 'locale'
	| 'timezone'
	| 'contextIds'
	| 'apolloClient'
> & {
	/**
	 * The Forge runtime environment (PRODUCTION / STAGING / DEVELOPMENT).
	 * Read from EnvironmentContext by the caller and passed in explicitly,
	 * so bridge methods can access it as plain context data.
	 */
	environment: ProductEnvironment;
	/**
	 * The analytics page/source identifier (e.g. 'confluence:editPageScreen').
	 * Read from MetricsContext by the caller and passed in explicitly,
	 * so bridge methods can access it as plain context data.
	 */
	page: string;
	// surfaceColor and theme are on InnerIframeProps but not IframeProps
	surfaceColor?: string | null;
	theme?: Partial<ThemeState> | null;
};

/**
 * Called when one or more props change during `update()`.
 * Receives the snapshot before the change, the new props, the pre-computed
 * per-key change record, and a lazy context accessor.
 *
 * Use `changes.<key>` rather than re-deriving equality — structural keys
 * (`coreData`, `extension`) are already deep-compared here, so reference
 * comparison in the listener would give wrong results.
 */
export type ChangeListener = (
	prevProps: Readonly<BridgeContextInput>,
	nextProps: Readonly<BridgeContextInput>,
	changes: Readonly<BridgeContextChanges>,
	getContext: () => BridgeContext,
) => void;

/**
 * Unified registry for per-instance bridge resources.
 * Each resource defines its own create and destroy lifecycle.
 * BridgeContextObject.destroy() calls destroy(state) for every claimed resource.
 */
class BridgeResourceRegistry {
	private readonly entries = new Map<
		symbol,
		{ resource: BridgeResource<unknown>; state: unknown }
	>();

	readonly claimBridgeResource: ClaimBridgeResource = <T>(resource: BridgeResource<T>): T => {
		if (this.entries.has(resource.id)) {
			return this.entries.get(resource.id)!.state as T;
		}
		const state = resource.create();
		this.entries.set(resource.id, { resource: resource as BridgeResource<unknown>, state });
		return state;
	};

	destroy(): void {
		for (const { resource, state } of this.entries.values()) {
			resource.destroy(state);
		}
		this.entries.clear();
	}
}

/**
 * Encapsulates the BridgeContext value, its lazy memoization, and change notifications.
 *
 * Derived fields (`resolverContext`, `viewContext`) are computed lazily on first access
 * rather than eagerly on every prop change. `update()` clears the cache so the next
 * `get()` call recomputes from the latest props.
 *
 * All memoization is done as plain reference-equality comparisons — no React hooks needed.
 * This makes the logic fully testable without a React tree.
 *
 * Usage:
 *   const obj = new BridgeContextObject(initialProps);
 *   obj.update(latestProps);  // call inside useEffect — diffs and emits events
 *   obj.get();                // call from getBridgeContext getter — always current
 */
export class BridgeContextObject {
	private props: BridgeContextInput;
	private readonly getServices: () => BridgeServices;
	private bridgeControls: BridgeControls | null = null;
	private readonly changeListeners = new Set<ChangeListener>();
	private readonly resolverContextCell = new LazyCell<ReturnType<typeof getResolverContext>>();
	private readonly viewContextCell = new LazyCell<ViewContext>();
	private readonly extensionDetailsCell = new LazyCell<ExtensionDetails>();
	private readonly resourceRegistry = new BridgeResourceRegistry();

	constructor(props: BridgeContextInput, getServices: () => BridgeServices) {
		this.props = props;
		this.getServices = getServices;
	}

	/**
	 * Registers the live bridge-core v2 connection.
	 *
	 * Called once on initial load and again on each iframe reload (e.g. tunnel mode hot-reload).
	 * On reload, the previous connection is closed before the new one is stored, so the old
	 * bridge-core channel is torn down without destroying the bridge context or resource registry.
	 */
	setBridgeControl(controls: BridgeControls): void {
		if (this.bridgeControls !== null) {
			// eslint-disable-next-line no-console
			console.debug('close and refresh existing bridge control');
			this.bridgeControls.close();
		}
		this.bridgeControls = controls;
	}

	/**
	 * Tears down all resources owned by this object. Should be called on component
	 * unmount to prevent memory leaks.
	 */
	destroy(): void {
		// Close the connection before destroying resources — prevents new bridge calls arriving after teardown.
		this.bridgeControls?.close();
		this.bridgeControls = null;
		this.resourceRegistry.destroy();
		this.changeListeners.clear();
	}

	/**
	 * Registers a listener that is called whenever one or more props change.
	 * The listener receives the previous props, the new props, and a lazy `getContext()`
	 * accessor — call it only if you need the context, as it allocates on first call.
	 * Returns an unsubscribe function — call it to stop receiving notifications.
	 *
	 * Listeners only fire when something actually changed (`changes.__all__` is true).
	 * They are cleared automatically on `destroy()`.
	 */
	onChange(listener: ChangeListener): () => void {
		this.changeListeners.add(listener);
		return () => this.changeListeners.delete(listener);
	}

	/**
	 * Diffs incoming props against stored props, clears the cache, and emits
	 * change events for updated BridgeContext fields.
	 *
	 * Must be called inside useEffect (not during render) because it emits DOM events.
	 */
	update(nextProps: BridgeContextInput): void {
		const prevProps = this.props;
		const changes = computeBridgeContextChanges(prevProps, nextProps);
		this.props = nextProps;

		if (changes.__all__) {
			// Brief always-on log; deep diff opt-in via DEBUG_ENABLED in BridgeContextDebug.ts.
			logBridgeContextChange(prevProps, nextProps, changes);

			this.resolverContextCell.invalidate();
			this.viewContextCell.invalidate();
			this.extensionDetailsCell.invalidate();

			// Lazy: computed at most once, only if a listener actually reads it.
			let contextCache: BridgeContext | undefined;
			// note, ??= is currently not supported in our build environment, hence will need to be rewritten when we upgrade to a newer TypeScript version
			const getContext = () =>
				contextCache !== undefined ? contextCache : (contextCache = this.get());
			for (const listener of this.changeListeners) {
				listener(prevProps, nextProps, changes, getContext);
			}
		}
	}

	/**
	 * Returns the current BridgeContext.
	 *
	 * Fields that involve non-trivial work are getter properties — deferred until
	 * the bridge method actually reads them.
	 *
	 * Pass-through fields are direct values, since reading a prop is O(1).
	 */
	get(): BridgeContext {
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const self = this;
		return {
			// Deferred — resolved only when the bridge method accesses the field.
			get resolverContext() {
				return self.resolverContext;
			},
			get viewContext() {
				return self.viewContext;
			},
			get extensionDetails() {
				return self.extensionDetails;
			},
			get services() {
				return self.getServices();
			},
			// Immediate — direct prop reads, no benefit to deferring.
			bridgeConfig: this.props.bridge ?? {},
			bridgeControls: this.bridgeControls!,
			claimBridgeResource: this.resourceRegistry.claimBridgeResource,
			contextIds: this.props.contextIds,
			environment: this.props.environment,
			page: this.props.page,
			extension: this.props.extension,
			extensionPayload: this.props.extensionPayload,
			// Exposed on context rather than via services to give change listeners
			// a scoped emit without needing to import the events module directly.
			emit: (event: string, payload?: unknown) => emitEvent(event, payload, self.extensionDetails),
		};
	}

	private get resolverContext(): ReturnType<typeof getResolverContext> {
		return this.resolverContextCell.get(() =>
			getResolverContext(
				{ ...this.props.coreData, appVersion: this.props.extension.appVersion } as CoreDataInner,
				this.props.extensionData,
			),
		);
	}

	private get extensionDetails(): ExtensionDetails {
		return this.extensionDetailsCell.get(() => {
			const { extension } = this.props;
			return {
				appId: extension.appId ?? parseExtensionId(extension.id).appId,
				id: extension.id.split('/').slice(0, 3).join('/'),
				installationId: extension.installationId,
			};
		});
	}

	private get viewContext(): ViewContext {
		return this.viewContextCell.get(() => this.buildViewContext(this.props, this.resolverContext));
	}

	private buildViewContext(
		props: BridgeContextInput,
		resolverContext: ReturnType<typeof getResolverContext>,
	): ViewContext {
		return getViewContext({
			resolverContext,
			accountId: props.accountId,
			extension: props.extension,
			timezone: props.timezone ?? getBrowserTimezone(),
			locale: props.locale ?? getBrowserLocale(),
			extensionPayload: props.extensionPayload,
			extensionViewData: props.extensionViewData,
			theme: props.theme,
			surfaceColor: props.surfaceColor,
			location: window.location.toString(),
		});
	}
}
